import { FALLBACK_SRC } from "./assetUtils";
import {
  borderStyles,
  emailSafeFontFamilies,
  fontStyles,
  fontWeights,
  textTransforms,
  underlineOptions,
} from "./fieldInputOptions";
import type { BrandKitField } from "@/types/onboarding";

const brandStyleFieldDefinitions: BrandKitField[] = [
  {
    id: "darkLogo",
    dsKey: "logo_dark",
    property: "src",
    selector: ".bk-logo",
    label: "Dark logo",
    field: {
      type: "text",
      defaultValue: FALLBACK_SRC,
    },
    value: FALLBACK_SRC,
    category: {
      id: "logo",
      dsKey: "logo",
      label: "Logo",
    },
  },
  {
    id: "lightLogo",
    dsKey: "logo_light",
    property: "src",
    selector: ".bk-logo-light",
    label: "Light logo",
    field: {
      type: "text",
      defaultValue: FALLBACK_SRC,
    },
    value: FALLBACK_SRC,
    category: {
      id: "logo",
      dsKey: "logo",
      label: "Logo",
    },
  },
  {
    id: "icon",
    dsKey: "logo_icon",
    property: "src",
    selector: ".bk-logo-icon",
    label: "Icon",
    field: {
      type: "text",
      defaultValue: FALLBACK_SRC,
    },
    value: FALLBACK_SRC,
    category: {
      id: "logo",
      dsKey: "logo",
      label: "Logo",
    },
  },
  {
    id: "logoWidth",
    dsKey: "logo_width",
    property: "width",
    selector: ".bk-logo",
    label: "Logo width",
    field: {
      type: "number",
      defaultValue: "200",
      units: ["px"],
    },
    value: "200",
    category: {
      id: "logo",
      dsKey: "logo",
      label: "Logo",
    },
  },
  {
    id: "canvasBackground",
    dsKey: "canvas_background",
    property: "background-color",
    selector: "mj-body",
    label: "Canvas",
    field: {
      type: "color",
      defaultValue: "#F7F5FA",
    },
    value: "#F7F5FA",
    category: {
      id: "colors",
      dsKey: "colors",
      label: "Colors",
    },
  },
  {
    id: "wrapperBackground",
    dsKey: "wrapper_background",
    property: "background-color",
    selector: ".bk-wrapper",
    label: "Wrapper",
    field: {
      type: "color",
      defaultValue: "#FFFFFF",
    },
    value: "#FFFFFF",
    category: {
      id: "colors",
      dsKey: "colors",
      label: "Colors",
    },
  },
  {
    id: "bannerBackground",
    dsKey: "banner_background",
    property: "background-color",
    selector: ".bk-banner",
    label: "Banner",
    field: {
      type: "color",
      defaultValue: "#C694FA",
    },
    value: "#C694FA",
    category: {
      id: "colors",
      dsKey: "colors",
      label: "Colors",
    },
  },
  {
    id: "alertBannerBackground",
    dsKey: "alert_banner_background",
    property: "background-color",
    selector: ".bk-alert-banner",
    label: "Alert banner",
    field: {
      type: "color",
      defaultValue: "#d74b3e",
    },
    value: "#d74b3e",
    category: {
      id: "colors",
      dsKey: "colors",
      label: "Colors",
    },
  },
  {
    id: "productBackground",
    dsKey: "product_background",
    property: "background-color",
    selector: ".bk-product",
    label: "Product",
    field: {
      type: "color",
      defaultValue: "#E6DEEC",
    },
    value: "#E6DEEC",
    category: {
      id: "colors",
      dsKey: "colors",
      label: "Colors",
    },
  },
  {
    id: "infoBackground",
    dsKey: "info_background",
    property: "background-color",
    selector: ".bk-info",
    label: "Info",
    field: {
      type: "color",
      defaultValue: "#F5F0F9",
    },
    value: "#F5F0F9",
    category: {
      id: "colors",
      dsKey: "colors",
      label: "Colors",
    },
  },
  {
    id: "headlineDarkColor",
    dsKey: "headline_dark",
    property: "color",
    selector: ".bk-headline",
    label: "Headline dark",
    field: {
      type: "color",
      defaultValue: "#252327",
    },
    value: "#252327",
    category: {
      id: "colors",
      dsKey: "colors",
      label: "Colors",
    },
  },
  {
    id: "headlineLightColor",
    dsKey: "headline_light",
    property: "color",
    selector: ".bk-headline-light",
    label: "Headline light",
    field: {
      type: "color",
      defaultValue: "#F5F0F9",
    },
    value: "#F5F0F9",
    category: {
      id: "colors",
      dsKey: "colors",
      label: "Colors",
    },
  },
  {
    id: "textDarkColor",
    dsKey: "text_dark",
    property: "color",
    selector: "mj-all",
    label: "Text dark",
    field: {
      type: "color",
      defaultValue: "#252327",
    },
    value: "#252327",
    category: {
      id: "colors",
      dsKey: "colors",
      label: "Colors",
    },
  },
  {
    id: "textLightColor",
    dsKey: "text_light",
    property: "color",
    selector: ".bk-text-light",
    label: "Text light",
    field: {
      type: "color",
      defaultValue: "#F5F0F9",
    },
    value: "#F5F0F9",
    category: {
      id: "colors",
      dsKey: "colors",
      label: "Colors",
    },
  },
  {
    id: "outlineColor",
    dsKey: "outline",
    property: "border",
    selector: "mj-divider",
    label: "Outline",
    field: {
      type: "color",
      defaultValue: "#9389BC",
    },
    value: "#9389BC",
    category: {
      id: "colors",
      dsKey: "colors",
      label: "Colors",
    },
  },
  {
    id: "headlineFontFamily",
    dsKey: "headline_font_family",
    property: "font-family",
    selector: ".bk-headline",
    label: "Headline font",
    field: {
      type: "selectFont",
      defaultValue: "Arial, Helvetica, sans-serif",
      options: emailSafeFontFamilies,
    },
    value: "Arial, Helvetica, sans-serif",
    category: {
      id: "typography",
      dsKey: "typography",
      label: "Typography",
    },
  },
  {
    id: "bodyFontFamily",
    dsKey: "body_font_family",
    property: "font-family",
    selector: "mj-all",
    label: "Body font",
    field: {
      type: "selectFont",
      defaultValue: "Arial, Helvetica, sans-serif",
      options: emailSafeFontFamilies,
    },
    value: "Arial, Helvetica, sans-serif",
    category: {
      id: "typography",
      dsKey: "typography",
      label: "Typography",
    },
  },
  // The brand's own web fonts. Not CSS declarations: `property` is a tag the
  // field groups and the validator read, and each pair drives one <mj-font> in
  // the email head (ensureWebFontInMjHead) plus an extra option in every font
  // picker. A non-"font-family" property keeps them out of the font-family
  // group, whose inputs are strict selects.
  //
  // Two pairs, because a site normally loads two faces — a display one for the
  // headings and a text one for the body. crazycups.com sets its headings in
  // Dela Gothic One and its copy in Poppins; with a single pair the email got
  // one of them and a default for the other.
  {
    id: "webFontFamily",
    dsKey: "web_font_family",
    property: "web-font",
    selector: "",
    label: "Headline web font name",
    field: {
      type: "text",
      defaultValue: "",
    },
    value: "",
    category: {
      id: "typography",
      dsKey: "typography",
      label: "Typography",
    },
  },
  {
    id: "webFontUrl",
    dsKey: "web_font_url",
    property: "web-font-url",
    selector: "",
    label: "Headline web font stylesheet URL",
    field: {
      type: "text",
      defaultValue: "",
    },
    value: "",
    category: {
      id: "typography",
      dsKey: "typography",
      label: "Typography",
    },
  },
  {
    id: "bodyWebFontFamily",
    dsKey: "body_web_font_family",
    property: "web-font",
    selector: "",
    label: "Body web font name",
    field: {
      type: "text",
      defaultValue: "",
    },
    value: "",
    category: {
      id: "typography",
      dsKey: "typography",
      label: "Typography",
    },
  },
  {
    id: "bodyWebFontUrl",
    dsKey: "body_web_font_url",
    property: "web-font-url",
    selector: "",
    label: "Body web font stylesheet URL",
    field: {
      type: "text",
      defaultValue: "",
    },
    value: "",
    category: {
      id: "typography",
      dsKey: "typography",
      label: "Typography",
    },
  },
  {
    id: "fontSizeDesktopXS",
    dsKey: "font_size_xs",
    property: "font-size",
    selector: ".bk-font-size-xs",
    label: "Extra small",
    field: {
      type: "number",
      defaultValue: "12",
      units: ["px"],
    },
    value: "12",
    category: {
      id: "typography",
      dsKey: "typography",
      label: "Typography",
    },
  },
  {
    id: "fontSizeDesktopS",
    dsKey: "font_size_sm",
    property: "font-size",
    selector: ".bk-font-size-sm",
    label: "Small",
    field: {
      type: "number",
      defaultValue: "14",
      units: ["px"],
    },
    value: "14",
    category: {
      id: "typography",
      dsKey: "typography",
      label: "Typography",
    },
  },
  {
    id: "fontSizeDesktopM",
    dsKey: "font_size_md",
    property: "font-size",
    selector: ".bk-font-size-md",
    label: "Medium",
    field: {
      type: "number",
      defaultValue: "16",
      units: ["px"],
    },
    value: "16",
    category: {
      id: "typography",
      dsKey: "typography",
      label: "Typography",
    },
  },
  {
    id: "fontSizeDesktopDefault",
    dsKey: "font_size_default",
    property: "font-size",
    selector: "mj-all",
    label: "Default",
    field: {
      type: "number",
      defaultValue: "18",
      units: ["px"],
    },
    value: "18",
    category: {
      id: "typography",
      dsKey: "typography",
      label: "Typography",
    },
  },
  {
    id: "fontSizeDesktopL",
    dsKey: "font_size_lg",
    property: "font-size",
    selector: ".bk-font-size-lg",
    label: "Large",
    field: {
      type: "number",
      defaultValue: "20",
      units: ["px"],
    },
    value: "20",
    category: {
      id: "typography",
      dsKey: "typography",
      label: "Typography",
    },
  },
  {
    id: "fontSizeDesktopXL",
    dsKey: "font_size_xl",
    property: "font-size",
    selector: ".bk-font-size-xl",
    label: "Extra large",
    field: {
      type: "number",
      defaultValue: "24",
      units: ["px"],
    },
    value: "24",
    category: {
      id: "typography",
      dsKey: "typography",
      label: "Typography",
    },
  },
  {
    id: "fontSizeDesktopOversize",
    dsKey: "font_size_oversize",
    property: "font-size",
    selector: ".bk-headline",
    label: "Oversize",
    field: {
      type: "number",
      defaultValue: "34",
      units: ["px"],
    },
    value: "34",
    category: {
      id: "typography",
      dsKey: "typography",
      label: "Typography",
    },
  },
  // {
  //   id: "fontSizeMobileXS",
  //   property: "font-size",
  //   selector: ".bk-font-size-xs-mobile",
  //   label: "Extra small",
  //   field: {
  //     type: "number",
  //     defaultValue: "10",
  //     units: ["px"],
  //   },
  //   value: "10",
  //   category: {
  //     id: "typography",
  //     label: "Typography",
  //   },
  // },
  // {
  //   id: "fontSizeMobileS",
  //   property: "font-size",
  //   selector: ".bk-font-size-sm-mobile",
  //   label: "Small",
  //   field: {
  //     type: "number",
  //     defaultValue: "12",
  //     units: ["px"],
  //   },
  //   value: "12",
  //   category: {
  //     id: "typography",
  //     label: "Typography",
  //   },
  // },
  // {
  //   id: "fontSizeMobileM",
  //   property: "font-size",
  //   selector: ".bk-font-size-md-mobile",
  //   label: "Medium",
  //   field: {
  //     type: "number",
  //     defaultValue: "14",
  //     units: ["px"],
  //   },
  //   value: "14",
  //   category: {
  //     id: "typography",
  //     label: "Typography",
  //   },
  // },
  // {
  //   id: "fontSizeMobileL",
  //   property: "font-size",
  //   selector: ".bk-font-size-lg-mobile",
  //   label: "Large",
  //   field: {
  //     type: "number",
  //     defaultValue: "16",
  //     units: ["px"],
  //   },
  //   value: "16",
  //   category: {
  //     id: "typography",
  //     label: "Typography",
  //   },
  // },
  // {
  //   id: "fontSizeMobileXL",
  //   property: "font-size",
  //   selector: ".bk-font-size-xl-mobile",
  //   label: "Extra large",
  //   field: {
  //     type: "number",
  //     defaultValue: "20",
  //     units: ["px"],
  //   },
  //   value: "20",
  //   category: {
  //     id: "typography",
  //     label: "Typography",
  //   },
  // },
  // {
  //   id: "fontSizeMobileOversize",
  //   property: "font-size",
  //   selector: ".bk-font-size-oversize-mobile",
  //   label: "Oversize",
  //   field: {
  //     type: "number",
  //     defaultValue: "28",
  //     units: ["px"],
  //   },
  //   value: "28",
  //   category: {
  //     id: "typography",
  //     label: "Typography",
  //   },
  // },
  {
    id: "headlineFontWeight",
    dsKey: "headline_font_weight",
    property: "font-weight",
    selector: ".bk-headline",
    label: "Headline font weight",
    field: {
      type: "select",
      options: fontWeights,
      defaultValue: "bold",
    },
    value: "bold",
    category: {
      id: "typography",
      dsKey: "typography",
      label: "Typography",
    },
  },
  {
    id: "headlineTextTransform",
    dsKey: "headline_text_transform",
    property: "text-transform",
    selector: ".bk-headline",
    label: "Headline text transform",
    field: {
      type: "select",
      options: textTransforms,
      defaultValue: "uppercase",
    },
    value: "uppercase",
    category: {
      id: "typography",
      dsKey: "typography",
      label: "Typography",
    },
  },
  {
    id: "headlineFontStyle",
    dsKey: "headline_font_style",
    property: "font-style",
    selector: ".bk-headline",
    label: "Headline font style",
    field: {
      type: "select",
      options: fontStyles,
      defaultValue: "normal",
    },
    value: "normal",
    category: {
      id: "typography",
      dsKey: "typography",
      label: "Typography",
    },
  },
  {
    id: "buttonPrimaryBg",
    dsKey: "primary_button_background",
    property: "background-color",
    selector: ".bk-button-primary",
    label: "Background color",
    field: {
      type: "color",
      defaultValue: "#6100D4",
    },
    value: "#6100D4",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonPrimaryText",
    dsKey: "primary_button_text",
    property: "color",
    selector: ".bk-button-primary",
    label: "Text color",
    field: {
      type: "color",
      defaultValue: "#FFFFFF",
    },
    value: "#FFFFFF",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonPrimaryPadding",
    dsKey: "primary_button_padding",
    property: "inner-padding",
    selector: ".bk-button-primary",
    label: "V and H padding",
    field: {
      type: "text",
      defaultValue: "16px 24px",
    },
    value: "16px 24px",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonPrimaryFontFamily",
    dsKey: "primary_button_font_family",
    property: "font-family",
    selector: ".bk-button-primary",
    label: "Font family",
    field: {
      type: "select",
      options: emailSafeFontFamilies,
      defaultValue: "Arial, Helvetica, sans-serif",
    },
    value: "Arial, Helvetica, sans-serif",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonPrimaryFontWeight",
    dsKey: "primary_button_font_weight",
    property: "font-weight",
    selector: ".bk-button-primary",
    label: "Font weight",
    field: {
      type: "select",
      options: fontWeights,
      defaultValue: "normal",
    },
    value: "normal",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonPrimaryTextTransform",
    dsKey: "primary_button_text_transform",
    property: "text-transform",
    selector: ".bk-button-primary",
    label: "Text transform",
    field: {
      type: "select",
      options: textTransforms,
      defaultValue: "uppercase",
    },
    value: "uppercase",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonPrimaryBorderColor",
    dsKey: "primary_button_border_color",
    property: "border-color",
    selector: ".bk-button-primary",
    label: "Border color",
    field: {
      type: "color",
      defaultValue: "#6100D4",
    },
    value: "#6100D4",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonPrimaryBorderStyle",
    dsKey: "primary_button_border_style",
    property: "border-style",
    selector: ".bk-button-primary",
    label: "Border style",
    field: {
      type: "select",
      defaultValue: "solid",
      options: borderStyles,
    },
    value: "solid",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonPrimaryBorderWidth",
    dsKey: "primary_button_border_width",
    property: "border-width",
    selector: ".bk-button-primary",
    label: "Border width",
    field: {
      type: "number",
      defaultValue: "1",
      units: ["px"],
    },
    value: "1",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonPrimaryBorderRadius",
    dsKey: "primary_button_border_radius",
    property: "border-radius",
    selector: ".bk-button-primary",
    label: "Border radius",
    field: {
      type: "number",
      defaultValue: "4",
      units: ["px"],
    },
    value: "4",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonSecondaryBg",
    dsKey: "secondary_button_background",
    property: "background-color",
    selector: ".bk-button-secondary",
    label: "Background color",
    field: {
      type: "color",
      defaultValue: "transparent",
    },
    value: "transparent",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonSecondaryText",
    dsKey: "secondary_button_text",
    property: "color",
    selector: ".bk-button-secondary",
    label: "Text color",
    field: {
      type: "color",
      defaultValue: "#7f1bf6",
    },
    value: "#7f1bf6",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonSecondaryPadding",
    dsKey: "secondary_button_padding",
    property: "inner-padding",
    selector: ".bk-button-secondary",
    label: "V and H padding",
    field: {
      type: "text",
      defaultValue: "16px 24px",
    },
    value: "16px 24px",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonSecondaryFontFamily",
    dsKey: "secondary_button_font_family",
    property: "font-family",
    selector: ".bk-button-secondary",
    label: "Font family",
    field: {
      type: "select",
      options: emailSafeFontFamilies,
      defaultValue: "Arial, Helvetica, sans-serif",
    },
    value: "Arial, Helvetica, sans-serif",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonSecondaryFontWeight",
    dsKey: "secondary_button_font_weight",
    property: "font-weight",
    selector: ".bk-button-secondary",
    label: "Font weight",
    field: {
      type: "select",
      options: fontWeights,
      defaultValue: "normal",
    },
    value: "normal",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonSecondaryTextTransform",
    dsKey: "secondary_button_text_transform",
    property: "text-transform",
    selector: ".bk-button-secondary",
    label: "Text transform",
    field: {
      type: "select",
      options: textTransforms,
      defaultValue: "uppercase",
    },
    value: "uppercase",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonSecondaryBorderColor",
    dsKey: "secondary_button_border_color",
    property: "border-color",
    selector: ".bk-button-secondary",
    label: "Border color",
    field: {
      type: "color",
      defaultValue: "#7f1bf6",
    },
    value: "#7f1bf6",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonSecondaryBorderStyle",
    dsKey: "secondary_button_border_style",
    property: "border-style",
    selector: ".bk-button-secondary",
    label: "Border style",
    field: {
      type: "select",
      defaultValue: "solid",
      options: borderStyles,
    },
    value: "solid",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonSecondaryBorderWidth",
    dsKey: "secondary_button_border_width",
    property: "border-width",
    selector: ".bk-button-secondary",
    label: "Border width",
    field: {
      type: "number",
      defaultValue: "1",
      units: ["px"],
    },
    value: "1",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonSecondaryBorderRadius",
    dsKey: "secondary_button_border_radius",
    property: "border-radius",
    selector: ".bk-button-secondary",
    label: "Border Radius",
    field: {
      type: "number",
      defaultValue: "4",
      units: ["px"],
    },
    value: "4",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonTertiaryBg",
    dsKey: "tertiary_button_background",
    property: "background-color",
    selector: ".bk-button-tertiary",
    label: "Background color",
    field: {
      type: "color",
      defaultValue: "#f5f0f9",
    },
    value: "#f5f0f9",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonTertiaryText",
    dsKey: "tertiary_button_text",
    property: "color",
    selector: ".bk-button-tertiary",
    label: "Text color",
    field: {
      type: "color",
      defaultValue: "#252327",
    },
    value: "#252327",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonTertiaryPadding",
    dsKey: "tertiary_button_padding",
    property: "inner-padding",
    selector: ".bk-button-tertiary",
    label: "V and H padding",
    field: {
      type: "text",
      defaultValue: "18px 20px",
    },
    value: "18px 20px",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonTertiaryFontFamily",
    dsKey: "tertiary_button_font_family",
    property: "font-family",
    selector: ".bk-button-tertiary",
    label: "Font family",
    field: {
      type: "select",
      options: emailSafeFontFamilies,
      defaultValue: "Arial, Helvetica, sans-serif",
    },
    value: "Arial, Helvetica, sans-serif",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonTertiaryFontWeight",
    dsKey: "tertiary_button_font_weight",
    property: "font-weight",
    selector: ".bk-button-tertiary",
    label: "Font weight",
    field: {
      type: "select",
      options: fontWeights,
      defaultValue: "normal",
    },
    value: "normal",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonTertiaryTextTransform",
    dsKey: "tertiary_button_text_transform",
    property: "text-transform",
    selector: ".bk-button-tertiary",
    label: "Text transform",
    field: {
      type: "select",
      options: textTransforms,
      defaultValue: "uppercase",
    },
    value: "uppercase",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonTertiaryBorderColor",
    dsKey: "tertiary_button_border_color",
    property: "border-color",
    selector: ".bk-button-tertiary",
    label: "Border color",
    field: {
      type: "color",
      defaultValue: "#6100D4",
    },
    value: "#6100D4",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonTertiaryBorderStyle",
    dsKey: "tertiary_button_border_style",
    property: "border-style",
    selector: ".bk-button-tertiary",
    label: "Border style",
    field: {
      type: "select",
      defaultValue: "solid",
      options: borderStyles,
    },
    value: "solid",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonTertiaryBorderWidth",
    dsKey: "tertiary_button_border_width",
    property: "border-width",
    selector: ".bk-button-tertiary",
    label: "Border width",
    field: {
      type: "number",
      defaultValue: "1",
      units: ["px"],
    },
    value: "1",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "buttonTertiaryBorderRadius",
    dsKey: "tertiary_button_border_radius",
    property: "border-radius",
    selector: ".bk-button-tertiary",
    label: "Border radius",
    field: {
      type: "number",
      defaultValue: "0",
      units: ["px"],
    },
    value: "0",
    category: {
      id: "buttons",
      dsKey: "buttons",
      label: "Buttons",
    },
  },
  {
    id: "textLinkColor",
    dsKey: "text_link_color",
    property: "color",
    selector: ".bk-link",
    label: "Text color",
    field: {
      type: "color",
      defaultValue: "#6100D4",
    },
    value: "#6100D4",
    category: {
      id: "textLink",
      dsKey: "text_link",
      label: "Text link",
    },
  },
  {
    id: "textLinkUnderline",
    dsKey: "text_link_underline",
    property: "text-decoration",
    selector: ".bk-link",
    label: "Text decoration",
    field: {
      type: "select",
      defaultValue: "underline",
      options: underlineOptions,
    },
    value: "underline",
    category: {
      id: "textLink",
      dsKey: "text_link",
      label: "Text link",
    },
  },
  {
    id: "textLinkFontWeight",
    dsKey: "text_link_font_weight",
    property: "font-weight",
    selector: ".bk-link",
    label: "Font weight",
    field: {
      type: "select",
      options: fontWeights,
      defaultValue: "bold",
    },
    value: "bold",
    category: {
      id: "textLink",
      dsKey: "text_link",
      label: "Text link",
    },
  },
  {
    id: "alertBannerTextFontWeight",
    dsKey: "alert_banner_font_weight",
    property: "font-weight",
    selector: ".bk-alert-banner-text",
    label: "Font weight",
    field: {
      type: "select",
      options: fontWeights,
      defaultValue: "bold",
    },
    value: "bold",
    category: {
      id: "alertBannerText",
      dsKey: "alert_banner",
      label: "Alert banner text",
    },
  },
  {
    id: "alertBannerTextTransform",
    dsKey: "alert_banner_text_transform",
    property: "text-transform",
    selector: ".bk-alert-banner-text",
    label: "Text transform",
    field: {
      type: "select",
      options: textTransforms,
      defaultValue: "uppercase",
    },
    value: "uppercase",
    category: {
      id: "alertBannerText",
      dsKey: "alert_banner",
      label: "Alert banner text",
    },
  },
  // Site chrome measured by Prisma. A brand book saved before these keys
  // existed has no value for them, so every field that would otherwise repaint
  // the email names the sibling it borrows from (inheritFrom, applied in
  // useFormData). Padding is deliberately not a field: each frame carries its
  // own header/footer padding and there is no no-op default for it.
  {
    id: "headerBackground",
    dsKey: "header_background",
    property: "background-color",
    selector: ".bk-header",
    label: "Background",
    field: {
      type: "color",
      defaultValue: "#ffffff",
    },
    value: "#ffffff",
    inheritFrom: "wrapperBackground",
    category: {
      id: "header",
      dsKey: "header",
      label: "Header",
    },
  },
  {
    id: "headerTextColor",
    dsKey: "header_text",
    property: "color",
    selector: ".bk-header-text",
    label: "Text color",
    field: {
      type: "color",
      defaultValue: "#252327",
    },
    value: "#252327",
    inheritFrom: "textDarkColor",
    category: {
      id: "header",
      dsKey: "header",
      label: "Header",
    },
  },
  {
    id: "headerLinkColor",
    dsKey: "header_link",
    property: "color",
    selector: ".bk-header-link",
    label: "Link color",
    field: {
      type: "color",
      defaultValue: "#252327",
    },
    value: "#252327",
    inheritFrom: "textLinkColor",
    category: {
      id: "header",
      dsKey: "header",
      label: "Header",
    },
  },
  {
    id: "headerBorderColor",
    dsKey: "header_border_color",
    property: "border-color",
    selector: ".bk-header-border",
    label: "Border color",
    field: {
      type: "color",
      defaultValue: "#e0e0e0",
    },
    value: "#e0e0e0",
    category: {
      id: "header",
      dsKey: "header",
      label: "Header",
    },
  },
  {
    id: "headerBorderWidth",
    dsKey: "header_border_width",
    property: "border-width",
    selector: ".bk-header-border",
    label: "Border width",
    field: {
      type: "number",
      defaultValue: "0",
      units: ["px"],
    },
    value: "0",
    category: {
      id: "header",
      dsKey: "header",
      label: "Header",
    },
  },
  {
    id: "footerBackground",
    dsKey: "footer_background",
    property: "background-color",
    selector: ".bk-footer",
    label: "Background",
    field: {
      type: "color",
      defaultValue: "transparent",
    },
    value: "transparent",
    category: {
      id: "footer",
      dsKey: "footer",
      label: "Footer",
    },
  },
  {
    id: "footerTextColor",
    dsKey: "footer_text",
    property: "color",
    selector: ".bk-footer-text",
    label: "Text color",
    field: {
      type: "color",
      defaultValue: "#252327",
    },
    value: "#252327",
    inheritFrom: "textDarkColor",
    category: {
      id: "footer",
      dsKey: "footer",
      label: "Footer",
    },
  },
  {
    id: "footerLinkColor",
    dsKey: "footer_link",
    property: "color",
    selector: ".bk-footer-link",
    label: "Link color",
    field: {
      type: "color",
      defaultValue: "#252327",
    },
    value: "#252327",
    inheritFrom: "textLinkColor",
    category: {
      id: "footer",
      dsKey: "footer",
      label: "Footer",
    },
  },
  {
    id: "footerBorderColor",
    dsKey: "footer_border_color",
    property: "border-color",
    selector: ".bk-footer-border",
    label: "Border color",
    field: {
      type: "color",
      defaultValue: "#e0e0e0",
    },
    value: "#e0e0e0",
    category: {
      id: "footer",
      dsKey: "footer",
      label: "Footer",
    },
  },
  {
    id: "footerBorderWidth",
    dsKey: "footer_border_width",
    property: "border-width",
    selector: ".bk-footer-border",
    label: "Border width",
    field: {
      type: "number",
      defaultValue: "0",
      units: ["px"],
    },
    value: "0",
    category: {
      id: "footer",
      dsKey: "footer",
      label: "Footer",
    },
  },
];

export default brandStyleFieldDefinitions;
